import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SimpleSelect } from "@/components/ui/simple-select";
import { CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";

const PERIOD_OPTIONS = [
  { value: "7", label: "Letzte 7 Tage" },
  { value: "30", label: "Letzte 30 Tage" },
  { value: "90", label: "Letzte 90 Tage" },
  { value: "365", label: "Letztes Jahr" },
];

export function VendorHygieneWidget() {
  const { company } = useCompany();
  const [data, setData] = useState(null);
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!company?._id) return;
    setLoading(true);
    try {
      const r = await apiClient.get(
        `/companies/${company._id}/reports/vendor-hygiene?days=${days}`
      );
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [company?._id, days]);

  const fmt = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleDateString("de-CH");
    } catch {
      return iso;
    }
  };

  return (
    <Card className="p-5 border-slate-200" data-testid="vendor-hygiene-widget">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            🔍 Stammdaten-Hygiene — Neue Lieferanten
          </h3>
          <p className="text-xs text-slate-500">
            Welche Lieferanten wurden zuletzt neu angelegt? Wo fehlen IBAN/MWST/Adresse?
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <SimpleSelect
            value={days}
            onValueChange={setDays}
            options={PERIOD_OPTIONS}
            data-testid="vendor-hygiene-period-select"
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={load}
            disabled={loading}
            data-testid="vendor-hygiene-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="text-center text-slate-400 py-8">
          <Loader2 className="h-5 w-5 inline animate-spin" />
        </div>
      ) : !data || data.new_suppliers.length === 0 ? (
        <div
          className="text-center text-sm text-slate-400 py-8"
          data-testid="vendor-hygiene-empty"
        >
          Keine neuen Lieferanten in diesem Zeitraum.
        </div>
      ) : (
        <>
          <div className="mb-3 text-xs text-slate-600 flex items-center gap-2 flex-wrap">
            <Badge variant="outline" data-testid="vendor-hygiene-total">
              {data.new_suppliers.length} neue Lieferanten
            </Badge>
            {data.incomplete_count > 0 && (
              <Badge
                className="bg-amber-100 text-amber-800"
                data-testid="vendor-hygiene-incomplete"
              >
                {data.incomplete_count} mit unvollständigen Stammdaten
              </Badge>
            )}
          </div>
          <table className="w-full text-sm" data-testid="vendor-hygiene-table">
            <thead className="text-xs text-slate-500 uppercase border-b border-slate-200">
              <tr>
                <th className="text-left p-2">Lieferant</th>
                <th className="text-left p-2">Angelegt</th>
                <th className="text-center p-2">Belege</th>
                <th className="text-center p-2">IBAN</th>
                <th className="text-center p-2">MWST</th>
                <th className="text-center p-2">Adresse</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.new_suppliers.map((s) => (
                <tr
                  key={s.contact_id}
                  className="border-b border-slate-100 hover:bg-slate-50/60"
                  data-testid={`vendor-hygiene-row-${s.contact_id}`}
                >
                  <td className="p-2 font-medium">{s.name}</td>
                  <td className="p-2 text-xs text-slate-500">{fmt(s.created_at)}</td>
                  <td className="p-2 text-center">
                    {s.receipts_count > 0 ? (
                      <Badge variant="outline" className="text-xs">
                        {s.receipts_count}
                      </Badge>
                    ) : (
                      <span className="text-slate-400 text-xs">0</span>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    {s.has_iban ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />
                    ) : (
                      <XCircle className="h-4 w-4 text-rose-400 inline" />
                    )}
                  </td>
                  <td className="p-2 text-center">
                    {s.has_vat_id ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />
                    ) : (
                      <XCircle className="h-4 w-4 text-rose-400 inline" />
                    )}
                  </td>
                  <td className="p-2 text-center">
                    {s.has_address ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />
                    ) : (
                      <XCircle className="h-4 w-4 text-rose-400 inline" />
                    )}
                  </td>
                  <td className="p-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(`/contacts?id=${s.contact_id}`, "_self")}
                      data-testid={`vendor-hygiene-edit-${s.contact_id}`}
                    >
                      Bearbeiten →
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}

export default VendorHygieneWidget;
